/* @jsx h */
import { defineComponent, h, onMounted, onUnmounted, ref, type PropType } from "vue";
import autocomplete from "autocompleter";
import type { GridColumnDef, GridOption, CommitDir } from "./types";

export const SelectEditor = defineComponent({
  name: "SelectEditor",
  props: {
    column: { type: Object as PropType<GridColumnDef>, required: true },
    value: { type: [String, Number, null] as any, default: null },
    onCommit: {
      type: Function as PropType<(value: any, dir: CommitDir) => void>,
      required: true,
    },
    onCancel: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const inputRef = ref<HTMLInputElement>();
    const options: GridOption[] = props.column.options ?? [];

    const currentOption = options.find((o) => String(o.value) === String(props.value ?? ""));
    const displayValue = ref(currentOption?.label ?? "");

    let ac: { destroy: () => void } | null = null;

    onMounted(() => {
      if (!inputRef.value) return;

      const allItems: Array<GridOption & { value: string | number | null }> = [
        { value: null, label: props.column.required ? "Select…" : "No value" } as any,
        ...options,
      ];

      ac = autocomplete<GridOption>({
        input: inputRef.value,
        minLength: 0,
        showOnFocus: true,
        debounceWaitMs: 0,
        className: "lg-ac-dropdown",
        fetch: (text, callback) => {
          // The input opens pre-filled (and selected) with the current label so
          // typing replaces it. Don't filter on that untouched label: it hid
          // every option not containing it — opening a cell set to "Available"
          // offered only "Available" and "Unavailable".
          const untouched = text === (currentOption?.label ?? "");
          const q = untouched ? "" : text.toLowerCase();
          const filtered = q
            ? allItems.filter((o) => o.label.toLowerCase().includes(q))
            : allItems;
          callback(filtered as GridOption[]);
        },
        onSelect: (item: any) => {
          props.onCommit(item.value, null);
        },
        render: (item: any) => {
          const div = document.createElement("div");
          const isSelected = String(item.value ?? "") === String(props.value ?? "");

          if (item.icon) {
            const icon = document.createElement("span");
            icon.className = "material-symbols-outlined";
            icon.style.fontSize = "14px";
            icon.textContent = item.icon;
            div.appendChild(icon);
          }

          if (item.value === null) div.classList.add("ac-empty");

          const label = document.createElement("span");
          label.style.flex = "1";
          label.textContent = item.label;
          div.appendChild(label);

          if (isSelected && item.value !== null) {
            const check = document.createElement("span");
            check.className = "material-symbols-outlined";
            check.style.cssText = "font-size:12px;opacity:0.55;margin-left:auto;";
            check.textContent = "check";
            div.appendChild(check);
          }

          return div;
        },
        customize: (_input, inputRect, container) => {
          container.style.width = `${Math.max(inputRect.width, 180)}px`;
        },
      });

      inputRef.value.focus();
      inputRef.value.select();
    });

    onUnmounted(() => {
      ac?.destroy();
    });

    const commitByLabel = (dir: CommitDir) => {
      const matched = options.find(
        (o) => o.label.toLowerCase() === displayValue.value.toLowerCase(),
      );
      // If typed text matches an option exactly, commit it; otherwise keep existing value
      props.onCommit(matched ? matched.value : props.value, dir);
    };

    return () => (
      <input
        ref={inputRef}
        type="text"
        class="lg-editor-input"
        value={displayValue.value}
        placeholder="Search…"
        onInput={(e: any) => (displayValue.value = e.target.value)}
        onKeydown={(e: KeyboardEvent) => {
          if (e.key === "Escape") { e.stopPropagation(); props.onCancel(); }
          if (e.key === "Tab") { e.preventDefault(); e.stopPropagation(); commitByLabel(e.shiftKey ? "left" : "right"); }
          // Enter is handled by autocompleter (it selects the focused item)
        }}
      />
    );
  },
});
